import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Clock,
  ExternalLink,
  Flame,
  GraduationCap,
  Instagram,
  Layers,
  MessageSquare,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSession } from "@/stores/session";
import { useWorkspace } from "@/stores/workspace";
import { api } from "@/lib/api";
import { getDailyFeed } from "@/lib/content-daily";
import type { CuratedVideo } from "@/lib/gmat-resources";
import { WEBSITES } from "@/lib/gmat-resources";
import { VideoCard } from "@/components/content/VideoCard";
import { cn } from "@/lib/cn";

function mapApiVideo(v: {
  id: string;
  youtube_id: string;
  title: string;
  channel: string;
  channel_url: string;
  url: string;
  tag: string;
}): CuratedVideo {
  return {
    id: v.id,
    youtubeId: v.youtube_id,
    title: v.title,
    channel: v.channel,
    channelUrl: v.channel_url,
    url: v.url,
    tag: v.tag,
    tags: [],
  };
}

const DAILY_INSIGHT = {
  title: "Number properties just clicked",
  body:
    "You gained 12 points on Quant this week. The shift came from divisibility rules — your Wednesday session unlocked it. Today, let's lock in the harder cousin: prime factorization in Problem Solving.",
  duration: "15 min",
  primary: { label: "Start today's session", view: "tutor" as const },
  secondary: { label: "Or take a mock", view: "exam" as const },
};

const QUICK_LINKS = WEBSITES.slice(0, 4);

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Late night";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function LearnHub() {
  const user = useSession((s) => s.user);
  const setView = useWorkspace((s) => s.setView);
  const firstName = user?.name?.split(" ")[0] ?? "Alex";
  const feed = getDailyFeed();
  const { data: ytFeed, isLoading: videosLoading } = useQuery({
    queryKey: ["learn-videos", 2],
    queryFn: () => api.learnVideos(2),
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
  const videos: CuratedVideo[] = ytFeed?.videos?.map(mapApiVideo) ?? feed.videos;
  const videosFromApi = Boolean(ytFeed?.videos?.length);

  return (
    <div className="container space-y-16 pb-20 pt-10">
      {/* Editorial masthead */}
      <header>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground"
        >
          <span className="hero-rule" />
          <span>
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
          <span className="hidden text-muted-foreground/50 md:inline">·</span>
          <span className="hidden md:inline">Think Big daily</span>
        </motion.div>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-serif text-5xl leading-[0.98] tracking-tight md:text-7xl"
          >
            {greeting()}, <span className="italic text-primary">{firstName}</span>.
          </motion.h1>
          <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs">
            <Flame className="size-3.5 text-accent" />
            <span className="font-medium">11-day streak</span>
          </div>
        </div>
      </header>

      {/* Daily insight — Maya's editor letter */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grain relative overflow-hidden rounded-[28px] border border-border/70 bg-card/70 p-8 shadow-editorial-lg md:p-12"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 size-80 rounded-full bg-accent/15 blur-3xl"
        />
        <div className="pointer-events-none absolute -left-16 bottom-0 size-64 rounded-full bg-primary/10 blur-3xl" />

        <div className="relative flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl space-y-4">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
              <span className="hero-rule" />
              <Sparkles className="size-3.5 text-accent" />
              <span>Letter from Maya</span>
              <span className="hidden text-muted-foreground/50 md:inline">·</span>
              <span className="hidden items-center gap-1 md:inline-flex">
                <Clock className="size-3" /> {DAILY_INSIGHT.duration}
              </span>
            </div>
            <h2 className="font-serif text-4xl leading-tight md:text-5xl">
              {DAILY_INSIGHT.title}
            </h2>
            <p className="drop-cap text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
              {DAILY_INSIGHT.body}
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 md:items-end">
            <Button variant="default" size="lg" onClick={() => setView(DAILY_INSIGHT.primary.view)}>
              {DAILY_INSIGHT.primary.label}
              <ArrowRight className="size-4" />
            </Button>
            <button
              type="button"
              onClick={() => setView(DAILY_INSIGHT.secondary.view)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {DAILY_INSIGHT.secondary.label}
            </button>
          </div>
        </div>
      </motion.section>

      {/* Tip strip */}
      <a
        href={feed.tip.url}
        target="_blank"
        rel="noreferrer noopener"
        className="group flex items-start gap-4 rounded-2xl border border-border/60 bg-card/60 p-5 transition-all hover:-translate-y-0.5 hover:shadow-editorial"
      >
        <div className="grid size-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/20 to-purple-500/20">
          <Instagram className="size-5 text-pink-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Tip of the day · {feed.tip.handle}
          </div>
          <p className="mt-1 font-serif text-lg leading-snug">{feed.tip.text}</p>
        </div>
        <ExternalLink className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </a>

      {/* Content sections */}
      <div className="grid gap-12 lg:grid-cols-3">
        <div className="space-y-12 lg:col-span-2">
          <div>
            <div className="mb-5 flex items-end justify-between gap-4">
              <SectionTitle
                title="Watch"
                subtitle={
                  videosFromApi
                    ? "Fresh from the channels 700+ scorers subscribe to"
                    : videosLoading
                      ? "Loading videos…"
                      : "Rotated daily — the creators serious students trust"
                }
              />
              <button
                type="button"
                onClick={() => setView("resources")}
                className="text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                All channels →
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {videos.map((v, i) => (
                <VideoCard key={v.id} {...v} eager={i === 0} />
              ))}
            </div>
          </div>

          <div>
            <SectionTitle
              title="Read"
              subtitle="Strategy essays and admissions dispatches, curated"
            />
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {feed.articles.map((a) => (
                <ArticleCard key={a.id} {...a} />
              ))}
            </div>
          </div>

          <div>
            <SectionTitle
              title="From the community"
              subtitle="What's echoing across r/GMAT, GMAT Club, r/MBA today"
            />
            <div className="mt-5 space-y-2">
              {feed.community.map((c) => (
                <CommunityRow key={c.id} {...c} />
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grain rounded-2xl border border-border/60 bg-card/70 p-6 shadow-editorial"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Predicted score
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif text-6xl leading-none">724</span>
              <span className="text-sm text-muted-foreground">/ 805</span>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-success">
              <TrendingUp className="size-3.5" /> +18 this week
            </div>
            <button
              type="button"
              onClick={() => setView("score")}
              className="mt-4 flex w-full items-center justify-between text-sm font-medium text-primary hover:underline"
            >
              See breakdown <ArrowRight className="size-3.5" />
            </button>
          </motion.div>

          <SideCard
            onClick={() => setView("flashcards")}
            icon={<Layers className="size-5 text-accent" />}
            badge="12 due"
            title="Flashcards"
            body="Spaced repetition — 5 minutes locks in yesterday's concepts."
            action="Review now"
          />

          <SideCard
            onClick={() => setView("topics")}
            icon={<GraduationCap className="size-5 text-primary" />}
            title="Tutor topics"
            body="Pick a topic — Maya teaches it like a real 1-on-1."
            action="Browse"
          />

          <SideCard
            onClick={() => setView("resources")}
            icon={null}
            title="Full resource library"
            body="Channels, forums, blogs, Instagram, podcasts, official GMAC."
            action="Explore"
            variant="dashed"
          />

          <div>
            <div className="mb-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Useful links
            </div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group rounded-xl border border-border/60 bg-card/60 p-3 transition-colors hover:bg-secondary/50"
                >
                  <div className="text-xs font-medium leading-tight">{l.name}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                    {l.host}
                    <ExternalLink className="size-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h3 className="font-serif text-3xl tracking-tight">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function SideCard({
  onClick,
  icon,
  badge,
  title,
  body,
  action,
  variant,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  badge?: string;
  title: string;
  body: string;
  action: string;
  variant?: "dashed";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "block w-full rounded-2xl border p-6 text-left transition-all hover:-translate-y-0.5 hover:shadow-editorial",
        variant === "dashed"
          ? "border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10"
          : "border-border/60 bg-card/60 hover:bg-secondary/40",
      )}
    >
      <div className="flex items-center justify-between">
        {icon}
        {badge && <Badge variant="outline">{badge}</Badge>}
      </div>
      <div className="mt-3 font-serif text-xl">{title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{body}</div>
      <div className="mt-4 flex items-center text-sm font-medium text-primary">
        {action} <ArrowUpRight className="ml-1 size-3.5" />
      </div>
    </button>
  );
}

function ArticleCard({
  source,
  minutes,
  title,
  tag,
  url,
}: {
  source: string;
  minutes: number;
  title: string;
  tag: string;
  url: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        "group flex flex-col rounded-2xl border border-border/60 bg-card/60 p-5 text-left",
        "transition-all hover:-translate-y-0.5 hover:shadow-editorial",
      )}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {source}
        </div>
        <Badge variant="outline" className="text-[10px]">
          {tag}
        </Badge>
      </div>
      <div className="mt-3 flex-1 font-serif text-xl leading-snug">{title}</div>
      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
        <span>{minutes} min read</span>
        <ArrowUpRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </a>
  );
}

function CommunityRow({
  source,
  title,
  replies,
  url,
}: {
  source: string;
  title: string;
  replies?: number;
  url: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        "flex w-full items-center justify-between rounded-xl border border-border/60 bg-card/60 px-4 py-3 text-left",
        "transition-colors hover:bg-secondary/50",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {source}
        </div>
        <div className="mt-0.5 truncate text-sm">{title}</div>
      </div>
      {replies != null && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <MessageSquare className="size-3.5" /> {replies}
        </div>
      )}
    </a>
  );
}
